import { useCallback, useEffect, useState } from "react";
import { FileState, GetTrackResponse } from "../../proto/api_pb";
import { GetTrackVersionsResponse } from "../../proto/api_pb";
import { RpcError } from "grpc-web";
import { Box, Typography, LinearProgress } from "@mui/material";
import { deleteTrackVersion, fetchTrack } from "../../services/trackService";
import { fetchTrackVersions } from "../../services/trackService";
import { TrackVersions } from "../../components/TrackVersion/TrackVersion";
import { TrackVersionGroupProps } from "../../components/TrackVersion/types";
import { TrackObj, TrackVersionsObj } from "../../pages/MyTracks/types";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { TrackDeleteConfirmModal } from "../Modal/TrackDeleteConfirmModal";
import { toast } from "react-toastify";
import { styles } from "./styles";

export const TrackVersionGroup: React.FC<TrackVersionGroupProps> = ({
  id,
  selectedVersionId,
  handleChange,
  isPlaying,
  handleStop,
  evokeCallBack,
}) => {
  const [trackVersions, setTrackVersions] = useState<TrackVersionsObj[]>([]);
  const [track, setTrack] = useState<TrackObj>();
  const [isOpen, setIsOpen] = useState(false);
  const [versionToDeleteId, setVersionToDeleteId] = useState("");
  const [pageLoaded, setPageLoaded] = useState(false);

  const { getValidAccessToken } = useGetValidAccessToken();
  const handlePlayPause = (id: string) => {
    handleStop(id);
    const currentSong = trackVersions.find(
      (item) => item.track?.getId() === id
    );
    if (currentSong) {
      if (currentSong.isPlaying) {
        const updatedTrackVersions = trackVersions.map((n) =>
          n.track?.getId() === id ? { ...n, isPlaying: false } : n
        );
        setTrackVersions(updatedTrackVersions);
      } else {
        const updatedTrack = trackVersions.map((n) => ({
          ...n,
          isPlaying: n.track?.getId() === id,
        }));
        setTrackVersions(updatedTrack);
        if (track) {
          track.waveSurfer?.stop();
          setTrack({ ...track, isPlaying: false });
        }
      }
    }
  };

  const stopAllVersionSongs = useCallback(() => {
    setTrackVersions((prev) =>
      prev.map((n) => ({
        ...n,
        isPlaying: false,
        selected: selectedVersionId === n.track?.getId(),
      }))
    );
  }, [selectedVersionId]);

  useEffect(() => {
    stopAllVersionSongs();
    // eslint-disable-next-line
  }, [evokeCallBack]);

  const handleFetch = useCallback(async () => {
    const token = await getValidAccessToken();
    try {
      const response = (await fetchTrack(token, id!)) as GetTrackResponse;
      setTrack({
        track: response.getTrack(),
        trackUrl: response.getTrackVersion()!.getTrackUrl(),
        isPlaying: false,
        waveSurfer: undefined,
      });
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
  }, [getValidAccessToken, id]);

  const updateSelectedTrackVersion = () => {
    setTrackVersions(
      trackVersions.map((el) => ({
        ...el,
        isSelected: el.track?.getId() === selectedVersionId,
      }))
    );
  };

  const handleStopAll = useCallback(() => {
    if (isPlaying) stopAllVersionSongs();
  }, [isPlaying, stopAllVersionSongs]);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  useEffect(() => {
    handleStopAll();
  }, [handleStopAll]);

  const handleFetchTrackVersions = useCallback(async () => {
    const token = await getValidAccessToken();
    try {
      const response = (await fetchTrackVersions(
        token,
        id!
      )) as GetTrackVersionsResponse;
      const newTrackVersionList = response
        .getTrackVersionsList()
        .filter(
          (version) => version.getFileState() === FileState.FILE_STATE_ACTIVE
        )
        .map((el) => {
          return {
            track: el,
            isPlaying: false,
            isSelected: selectedVersionId === el.getId(),
          };
        });
      setTrackVersions(newTrackVersionList);
      setPageLoaded(true);
    } catch (e) {
      const error = e as RpcError;
      console.log(error.code);
      console.log(error.message);
    }
  }, [getValidAccessToken, id, selectedVersionId]);

  useEffect(() => {
    handleFetchTrackVersions();
  }, [handleFetchTrackVersions]);

  const handleDeleteVersion = async () => {
    const updatedVersions = trackVersions.filter(
      (version) => version.track?.getId() !== versionToDeleteId
    );
    setTrackVersions([...updatedVersions]);
    const deleteTrackVersionResponse = await deleteTrackVersion(
      versionToDeleteId,
      await getValidAccessToken()
    );
    if (deleteTrackVersionResponse instanceof RpcError) {
      throw deleteTrackVersionResponse;
    }
    handleCloseDeleteModal();
    toast.success("Version removed successfully");
  };

  const handleOpenDeleteModal = (id: string) => {
    stopAllVersionSongs();
    setIsOpen(true);
    setVersionToDeleteId(id);
  };
  const handleCloseDeleteModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <TrackDeleteConfirmModal
        deleteConfirmModalOpen={isOpen}
        closeDeleteConfirmModal={handleCloseDeleteModal}
        handleDeleteTrack={handleDeleteVersion}
        type="Version"
      />
      <Box>
        {pageLoaded && trackVersions.length > 0 && (
          <Typography sx={styles.emptyTypography} variant="h6"></Typography>
        )}
        <Box sx={styles.trackVersionsContainer}>
          {trackVersions &&
            trackVersions.map((el: any) => {
              const url = el.track.getTrackUrl();
              const bpm = el.track?.getAudioMetadata()?.getBpm();
              const id = el.track.getId();
              const label = el.track.getLabel();
              const timeStamp = el.track.getTimestamp();
              const ownerId = el.track.getOwnerId();
              const { isPlaying, isSelected } = el;
              return (
                <TrackVersions
                  key={el.track.getId()}
                  stopAllSongs={stopAllVersionSongs}
                  handlePlayPause={handlePlayPause}
                  version={url}
                  isPlaying={isPlaying}
                  id={id}
                  bpm={bpm}
                  label={label}
                  timeStamp={timeStamp}
                  ownerId={ownerId}
                  isSelected={isSelected}
                  selectedVersionId={selectedVersionId}
                  handleChange={handleChange}
                  updateSelectedTrackVersion={updateSelectedTrackVersion}
                  handleOpenDeleteModal={handleOpenDeleteModal}
                />
              );
            })}
        </Box>
        {!pageLoaded && (
          <Box style={styles.progressContainer}>
            <Box sx={styles.linearProgressContainer}>
              <LinearProgress color="inherit" />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};
